/**
 * AddLineToCatalogDialog — iter 82.7r.
 *
 * From an albaran line, add the product to the inventory catalog
 * (/productos). Categoría restringida a food_cost / limpieza — same
 * rule as the manual product flow. On duplicate the backend returns
 * 409 with `{existing, incoming, price_delta, options}`; the user picks
 * one of `update` / `keep` / `new`.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { formatMoney } from "@/components/vendors/utils";

export function AddLineToCatalogDialog({ payload, onClose, baseCurrency, lang }) {
  const { albaranId, lineIndex, line } = payload;
  const [category, setCategory] = useState("food_cost");
  const [busy, setBusy] = useState(false);
  // Duplicate branch payload from a 409 response.
  const [duplicate, setDuplicate] = useState(null);

  const submit = async (action = "auto") => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/albaranes/${albaranId}/add-to-catalog`,
        { line_index: lineIndex, category, action },
      );
      toast.success(
        lang === "es"
          ? {
              create: `Añadido al catálogo: ${data.name}`,
              update: `Precio actualizado a ${formatMoney(data.new_price, baseCurrency)}`,
              keep:   `Se mantiene el precio existente`,
              new:    `Nueva variante creada: ${data.name}`,
            }[data.action_taken]
          : `Done: ${data.action_taken}`,
      );
      onClose();
    } catch (err) {
      // Duplicate → open the resolution UI in-place.
      if (err?.response?.status === 409) {
        setDuplicate(err.response.data);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="albaran-add-to-catalog-dialog">
        <DialogHeader>
          <DialogTitle>
            {lang === "es" ? "Añadir a Productos y precios" : "Add to Products & prices"}
          </DialogTitle>
          <DialogDescription>
            {lang === "es"
              ? "La línea se guardará como producto del catálogo, sólo para categorías Food Cost o Limpieza."
              : "The line is stored in the catalog under the Food Cost or Limpieza category."}
          </DialogDescription>
        </DialogHeader>

        {/* Line preview */}
        <div className="border border-[var(--border-color)] rounded-md p-2 bg-[var(--surface-hover)]/40 text-sm">
          <div className="font-medium">{line.name}</div>
          <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
            {(line.qty ?? "—")} {line.unit || ""} · {line.unit_price != null ? formatMoney(line.unit_price, baseCurrency) : "—"}/{lang === "es" ? "ud" : "unit"}
          </div>
        </div>

        {!duplicate && (
          <>
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
                {lang === "es" ? "Categoría" : "Category"}
              </p>
              <div className="flex gap-2">
                {[
                  { v: "food_cost", label: "Food Cost" },
                  { v: "limpieza",  label: lang === "es" ? "Limpieza" : "Cleaning" },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setCategory(opt.v)}
                    data-testid={`add-catalog-cat-${opt.v}`}
                    className={`px-3 py-1.5 text-xs rounded-md border ${
                      category === opt.v
                        ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] font-semibold"
                        : "border-[var(--border-color)] hover:bg-[var(--surface-hover)]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={onClose} disabled={busy}>
                {lang === "es" ? "Cancelar" : "Cancel"}
              </Button>
              <Button
                onClick={() => submit("auto")}
                disabled={busy}
                data-testid="add-catalog-submit"
                className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              >
                {lang === "es" ? "Añadir" : "Add"}
              </Button>
            </DialogFooter>
          </>
        )}

        {duplicate && (
          <div className="mt-3 space-y-3" data-testid="add-catalog-duplicate-panel">
            <div className="border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
                {lang === "es"
                  ? "Este producto ya existe en el catálogo"
                  : "This product is already in the catalog"}
              </p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                    {lang === "es" ? "Precio actual" : "Current price"}
                  </p>
                  <p className="font-mono-num font-semibold">
                    {formatMoney(duplicate.existing.unit_price, baseCurrency)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
                    {lang === "es" ? "Precio nuevo (albarán)" : "New price (delivery)"}
                  </p>
                  <p className="font-mono-num font-semibold">
                    {formatMoney(duplicate.incoming.unit_price, baseCurrency)}
                    {duplicate.price_delta !== 0 && (
                      <span className={`ml-1.5 text-[10px] font-semibold ${
                        duplicate.price_delta > 0
                          ? "text-[var(--status-negative)]"
                          : "text-[var(--status-positive)]"
                      }`}>
                        {duplicate.price_delta > 0 ? "+" : ""}
                        {formatMoney(duplicate.price_delta, baseCurrency)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
            <p className="text-xs text-[var(--text-muted)]">
              {lang === "es" ? "Elige cómo continuar:" : "Choose how to continue:"}
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                onClick={() => submit("keep")}
                disabled={busy}
                data-testid="add-catalog-keep"
                className="text-xs"
              >
                {lang === "es" ? "Mantener" : "Keep"}
              </Button>
              <Button
                onClick={() => submit("update")}
                disabled={busy}
                data-testid="add-catalog-update"
                className="text-xs bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
              >
                {lang === "es" ? "Actualizar precio" : "Update price"}
              </Button>
              <Button
                variant="outline"
                onClick={() => submit("new")}
                disabled={busy}
                data-testid="add-catalog-new"
                className="text-xs"
              >
                {lang === "es" ? "Nueva variante" : "New variant"}
              </Button>
            </div>
            <p className="text-[10px] text-[var(--text-muted)] leading-tight">
              {lang === "es"
                ? "Mantener: no se toca el catálogo. Actualizar: sobrescribe el precio actual con el del albarán. Nueva variante: crea un producto separado con sufijo (v2)."
                : "Keep: catalog untouched. Update: overwrite the current price. New variant: create a separate product with a (v2) suffix."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
